import { useEffect } from 'react';
import { Route, Routes, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { ROUTES } from '@openclassrooms/routing';
import { getLoadableModule } from 'Modules/lazy-common';

import { RequireAuth, GlobalErrorHandler } from 'Modules/errors';
import Error404 from 'Modules/errors/screens/Error404';

import { ClassicScreen, FullScreen, FullWidthMenuScreen } from 'Modules/layout';

import ProjectLayoutScreen from 'Modules/projects/screens/ProjectLayoutScreen';

// Webpack chunks

const AboutUs = getLoadableModule(
  () => import(/* webpackChunkName: "aboutUs" */ 'Modules/aboutUs/screens/AboutUs')
);

const Courses = getLoadableModule(
  () => import(/* webpackChunkName: "courses" */ 'Modules/courses/screens')
);

const MatchDeclarations = getLoadableModule(
  () => import(/* webpackChunkName: "matchDeclarations" */ 'Modules/match-declarations/screens')
);

const Account = getLoadableModule(
  () => import(/* webpackChunkName: "account" */ 'Modules/user-data/modules/account/screens')
);

const Application = getLoadableModule(
  () => import(/* webpackChunkName: "application" */ 'Modules/application/screens/Application')
);

const JobApplications = getLoadableModule(
  () => import(/* webpackChunkName: "job-applications" */ 'Modules/job-applications/screens')
);

const ApplicationRouting = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "applicationDetails" */ 'Modules/application/screens/ApplicationRouting'
    )
);

const Apprenticeship = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "apprenticeship" */ 'Modules/apprenticeship/screen/ApprenticeshipScreen'
    )
);

const ResumeGeneration = getLoadableModule(
  () => import(/* webpackChunkName: "resumeGeneration" */ 'Modules/resume/screens')
);

const AuthCheckDetails = getLoadableModule(
  () => import(/* webpackChunkName: "authCheckDetails" */ 'Modules/auth/screens/CheckDetailsScreen')
);

const Business = getLoadableModule(
  () => import(/* webpackChunkName: "business" */ 'Modules/business/default/screens')
);

const CirrFunnel = getLoadableModule(
  () => import(/* webpackChunkName: "cirrFunnel" */ 'Modules/cirr/screens/CirrFunnel')
);

const Completion = getLoadableModule(
  () => import(/* webpackChunkName: "profile-completion" */ 'Modules/profile-completion/screens')
);

const Cpf = getLoadableModule(
  () => import(/* webpackChunkName: "cpf" */ 'Modules/cpf/screens/Cpf')
);

const EndTraining = getLoadableModule(
  () => import(/* webpackChunkName: "endTraining" */ 'Modules/end-training/screens')
);

const Enrollment = getLoadableModule(
  () => import(/* webpackChunkName: "enrollment" */ 'Modules/enrollment/screens')
);

const Funding = getLoadableModule(
  () => import(/* webpackChunkName: "funding" */ 'Modules/funding')
);

const GeneratePassword = getLoadableModule(
  () =>
    import(/* webpackChunkName: "generatePassword" */ 'Modules/security/screens/GeneratePassword')
);

const HomePage = getLoadableModule(
  () => import(/* webpackChunkName: "homePage" */ 'Modules/home/screens/HomePage')
);

const Invitations = getLoadableModule(
  () => import(/* webpackChunkName: "invitations" */ 'Modules/invitations/screens/Join')
);

const JuryAssessment = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "juryAssessment" */ 'Modules/jury-assessments/screens/JuryAssessment'
    )
);

const JobPostings = getLoadableModule(
  () => import(/* webpackChunkName: "jobPostings" */ 'Modules/jobBoard/screens')
);

const LearningExperience = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "learningExperience" */ 'Modules/learning-experience/screens/LearningExperience'
    )
);

const Login = getLoadableModule(
  () => import(/* webpackChunkName: "authLogin" */ 'Modules/auth/screens/LoginScreen')
);

const LoginRegisterContext = getLoadableModule(
  () => import(/* webpackChunkName: "auth" */ 'Modules/auth/screens/AuthScreen')
);

const MentorshipDashboard = getLoadableModule(
  () => import(/* webpackChunkName: "mentorshipDashboard" */ 'Modules/mentorship-dashboard/screens')
);

const Offers = getLoadableModule(
  () => import(/* webpackChunkName: "offers" */ 'Modules/offers/screens/Offers')
);

const Organizations = getLoadableModule(
  () => import(/* webpackChunkName: "organizations" */ 'Modules/organizations/screens')
);

const MyOrganizationRedirect = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "myOrganizationRedirect" */ 'Modules/organizations/screens/MyOrganizationRedirect'
    )
);

const PathOutcomes = getLoadableModule(
  () => import(/* webpackChunkName: "pathOutcomes" */ 'Modules/learning-path/screens/PathOutcomes')
);

const Parchments = getLoadableModule(
  () => import(/* webpackChunkName: "diploma" */ 'Modules/parchments/screens')
);

const Partners = getLoadableModule(
  () => import(/* webpackChunkName: "partners" */ 'Modules/partners/screens')
);

const Profile = getLoadableModule(
  () => import(/* webpackChunkName: "profile" */ 'Modules/user-data/modules/profile/screens')
);

const Projects = getLoadableModule(
  () => import(/* webpackChunkName: "projects" */ 'Modules/projects/screens')
);

const ProofOfCompletion = getLoadableModule(
  () => import(/* webpackChunkName: "proofOfCompletion" */ 'Modules/proofOfCompletion/screens')
);

const Register = getLoadableModule(
  () => import(/* webpackChunkName: "authRegister" */ 'Modules/auth/screens/RegisterScreen')
);

const ResetPassword = getLoadableModule(
  () => import(/* webpackChunkName: "resetPassword" */ 'Modules/security/screens/ResetPassword')
);

const Spaces = getLoadableModule(
  () => import(/* webpackChunkName: "spaces" */ 'Modules/spaces/Routing')
);

const StanfordCourses = getLoadableModule(
  () => import(/* webpackChunkName: "stanfordCourses" */ 'Modules/stanfordCourses/screens')
);

const StudentApplication = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "studentApplication" */ 'Modules/student-application/screens/StudentApplication'
    )
);

const ApprenticeshipCandidateOnboarding = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "apprenticeshipCandidateOnboarding" */ 'Modules/apprenticeshipApplication/Routing'
    )
);

const StudentConfirmation = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "studentConfirmation" */ 'Modules/studentConfirmation/screens/StudentConfirmation'
    )
);

const Subscriptions = getLoadableModule(
  () => import(/* webpackChunkName: "subscriptions" */ 'Modules/subscriptions/screens')
);

const Talents = getLoadableModule(
  () => import(/* webpackChunkName: "talents" */ 'Modules/talents/screens')
);

const Teams = getLoadableModule(
  () => import(/* webpackChunkName: "teams" */ 'Modules/Teams/screens')
);

const TutorDashboard = getLoadableModule(
  () => import(/* webpackChunkName: "tutorDashboard" */ 'Modules/tutor-dashboard/screens')
);

const UkBusiness = getLoadableModule(
  () => import(/* webpackChunkName: "ukBusiness" */ 'Modules/business/uk/screens')
);

const UsBusiness = getLoadableModule(
  () => import(/* webpackChunkName: "UsBusiness" */ 'Modules/business/us/screens/Business')
);

const UserSurveys = getLoadableModule(
  () => import(/* webpackChunkName: "userSurveys" */ 'Modules/user-surveys/screens')
);

const VapFunnel = getLoadableModule(
  () => import(/* webpackChunkName: "vapFunnel" */ 'Modules/vap/screens')
);

const VideoConference = getLoadableModule(
  () => import(/* webpackChunkName: "videoConference" */ 'Modules/video-conference/screens')
);

const Welcome = getLoadableModule(
  () => import(/* webpackChunkName: "welcome" */ 'Modules/welcome/screens')
);

const WelcomeLearningJourneyIntroduction = getLoadableModule(
  () =>
    import(
      /* webpackChunkName: "welcomeLearningJourneyIntroduction" */ 'Modules/welcome/screens/LearningJourneyIntroduction'
    )
);

const WelcomePathStart = getLoadableModule(
  () => import(/* webpackChunkName: "welcomePathStart" */ 'Modules/welcome/screens/PathStart')
);

const WelcomeStudent = getLoadableModule(
  () => import(/* webpackChunkName: "welcomeStudent" */ 'Modules/welcome/screens/StudentWelcome')
);

const Policies = getLoadableModule(
  () => import(/* webpackChunkName: "policies" */ 'Modules/policies/screens')
);

const Dashboard = getLoadableModule(
  () => import(/* webpackChunkName: "dashboard" */ 'Modules/dashboard')
);

const Security = getLoadableModule(
  () => import(/* webpackChunkName: "security" */ 'Modules/security/screens/SecurityPage')
);

function RedirectToSpace({ space }: { space: string }) {
  const { '*': rest = '' } = useParams();
  const [searchParams] = useSearchParams();

  // Can't use Navigate from react-router because some spaces pages are Twig ones and not in the webapp:
  // react-router is therefore not aware of these routes.

  useEffect(() => {
    window.location.href = `/spaces/${space}/${rest?.replace(/dashboard\/?/g, '')}${
      searchParams.size > 0 ? `?${searchParams}` : ''
    }`;
  }, [rest, searchParams, space]);

  return null;
}

function Routing({ globalError }: { globalError?: Error }) {
  if (globalError) {
    return (
      <Routes>
        <Route
          path="*"
          element={
            <ClassicScreen>
              <GlobalErrorHandler />
            </ClassicScreen>
          }
        />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route
        path="/"
        element={
          <RequireAuth>
            <ProjectLayoutScreen />
          </RequireAuth>
        }
      >
        <Route path={`${ROUTES.PROJECT.ADMIN.STUDENT.PATH.INDEX}/*`} element={<Projects />} />
        <Route path={`${ROUTES.PROJECT.ADMIN.STUDENT.INDEX}/*`} element={<Projects />} />
        <Route path={`${ROUTES.PROJECT.USER.PATH.INDEX}/*`} element={<Projects />} />
        <Route path={`${ROUTES.PROJECT.PATH.INDEX}/*`} element={<Projects />} />
        <Route path={`${ROUTES.PROJECT.USER.INDEX}/*`} element={<Projects />} />
        <Route path={`${ROUTES.PROJECT.INDEX}/*`} element={<Projects />} />
      </Route>

      <Route path="/" element={<ClassicScreen />}>
        <Route index element={<HomePage />} />

        <Route path={ROUTES.SECURITY.SECURITY_PAGE} element={<Security />} />

        <Route path={ROUTES.ABOUT_US.INDEX} element={<AboutUs />} />

        <Route path={ROUTES.APPRENTICESHIP.INDEX} element={<Apprenticeship />} />
        <Route path={ROUTES.UK_APPRENTICESHIP.INDEX} element={<Apprenticeship />} />

        <Route path={ROUTES.CPF.INDEX} element={<Cpf />} />
        <Route path={ROUTES.LEARNING_EXPERIENCE.INDEX} element={<LearningExperience />} />
        <Route path={ROUTES.OFFERS.INDEX} element={<Offers />} />
        <Route path={ROUTES.UK_BUSINESS.INDEX} element={<UkBusiness />} />
        <Route path={ROUTES.STANFORD_COURSES.INDEX} element={<StanfordCourses />} />
        <Route path={ROUTES.PATH.OUTCOMES} element={<PathOutcomes />} />
        <Route path={ROUTES.INVITATIONS.JOIN} element={<Invitations />} />
        <Route path={ROUTES.SECURITY.GENERATE_PASSWORD} element={<GeneratePassword />} />
        <Route path={ROUTES.SECURITY.SET_NEW_PASSWORD} element={<ResetPassword />} />
        <Route path={ROUTES.US_BUSINESS.INDEX} element={<UsBusiness />} />
        <Route path={`${ROUTES.JOB_POSTINGS.INDEX}/*`} element={<JobPostings />} />
        {[
          // Redirect old policies routes from new /policies/* ones
          '/terms-conditions',
          '/terms-of-sale',
          '/privacy-policy',
          '/internal-rules-for-students',
          '/legal-information',
          '/talent-marketplace-conditions',
          '/code-of-ethics',
          '/talent-marketplace-conditions-us',
        ].map((oldRoute) => (
          <Route
            key={oldRoute}
            path={oldRoute}
            element={<Navigate to={`/policies${oldRoute}`} />}
          />
        ))}
        <Route path="/policies/*" element={<Policies />} />
        <Route path="/funding/*" element={<Funding />} />

        <Route path="*" element={<Error404 />} />
      </Route>

      <Route
        path="/dashboard/*"
        element={
          <RequireAuth>
            <FullWidthMenuScreen hasRouter variant="default" withSideSpacing>
              <Dashboard />
            </FullWidthMenuScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/users/:userId/dashboard/*"
        element={
          <RequireAuth>
            <FullWidthMenuScreen hasRouter variant="default" withSideSpacing>
              <Dashboard />
            </FullWidthMenuScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/mentorship/dashboard/*"
        element={
          <RequireAuth>
            <FullWidthMenuScreen variant="list" hasRouter withSideSpacing>
              <MentorshipDashboard />
            </FullWidthMenuScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/courses/*"
        element={
          <ClassicScreen shouldHideFooter>
            <Courses />
          </ClassicScreen>
        }
      />

      <Route path="/" element={<FullScreen />}>
        <Route path={ROUTES.WELCOME.STUDENT_WELCOME} element={<WelcomeStudent />} />
        <Route
          path={ROUTES.WELCOME.LEARNING_JOURNEY_INTRODUCTION}
          element={<WelcomeLearningJourneyIntroduction />}
        />
        <Route path={ROUTES.STUDENT_CONFIRMATION.INDEX} element={<StudentConfirmation />} />
        <Route path={ROUTES.AUTH.CHECK_DETAILS} element={<AuthCheckDetails />} />
        <Route path={ROUTES.PROFILE_COMPLETION.INDEX} element={<Completion />} />
        <Route path={ROUTES.CIRR.INDEX} element={<CirrFunnel />} />
        <Route path={ROUTES.WELCOME.PATH_START} element={<WelcomePathStart />} />
        <Route path="/end-training/*" element={<EndTraining />} />
        <Route path="/application/*" element={<Application />} />
        <Route path="/applications/:id/*" element={<ApplicationRouting />} />
        <Route path={ROUTES.STUDENT_APPLICATION.QUESTION} element={<StudentApplication />} />
        <Route
          path="/apprenticeship-candidate-onboarding/*"
          element={<ApprenticeshipCandidateOnboarding />}
        />
        <Route path="/vap/:config/*" element={<VapFunnel />} />
        <Route path={ROUTES.RESUME.APPRENTICESHIP} element={<ResumeGeneration />} />

        <Route path="*" element={<Error404 />} />
      </Route>

      <Route path="/" element={<RequireAuth />}>
        <Route path="/account/*" element={<Account />} />
        <Route
          path="/profile/*"
          element={
            <ClassicScreen shouldHideFooter>
              <Profile />
            </ClassicScreen>
          }
        />

        <Route
          path="/spaces/*"
          element={
            <ClassicScreen>
              <Spaces />
            </ClassicScreen>
          }
        />
        <Route path={`${ROUTES.MATCH_DECLARATIONS.INDEX}/*`} element={<MatchDeclarations />} />
        <Route path="/admin/*" element={<RedirectToSpace space="admin" />} />
        <Route path="/admin/oc-dashboard/*" element={<RedirectToSpace space="admin" />} />
      </Route>

      <Route path="/" element={<LoginRegisterContext />}>
        <Route path={ROUTES.AUTH.LOGIN} element={<Login />} />
        <Route path="/register/*" element={<Register />} />
      </Route>

      <Route path={ROUTES.DIPLOMA.PREVIEW} element={<Parchments />} />

      <Route path="/business/*" element={<Business />} />
      <Route path={`${ROUTES.JOB_APPLICATIONS.INDEX}/*`} element={<JobApplications />} />
      <Route path="/my-organization/*" element={<MyOrganizationRedirect />} />

      <Route
        path="/organizations/:organizationId/*"
        element={
          <FullWidthMenuScreen variant="list" hasRouter>
            <Organizations />
          </FullWidthMenuScreen>
        }
      />

      <Route
        path="/talents/:userId?/:searchId?"
        element={
          <RequireAuth authContext="talents">
            <FullScreen>
              <Talents />
            </FullScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/tutor/dashboard/*"
        element={
          <RequireAuth>
            <FullWidthMenuScreen variant="list" hasRouter withSideSpacing>
              <TutorDashboard />
            </FullWidthMenuScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/video-conferences/:id/meet/*"
        element={
          <RequireAuth>
            <FullScreen>
              <VideoConference />
            </FullScreen>
          </RequireAuth>
        }
      />

      <Route
        path={ROUTES.JURY_ASSESSMENTS.INDEX}
        element={
          <RequireAuth>
            <ClassicScreen>
              <JuryAssessment />
            </ClassicScreen>
          </RequireAuth>
        }
      />

      <Route
        path={ROUTES.JURY_ASSESSMENTS.SEE_ASSESSMENTS}
        element={
          <RequireAuth>
            <ClassicScreen>
              <JuryAssessment />
            </ClassicScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/users/:userId/survey/career/path/:pathId/*"
        element={
          <RequireAuth>
            <FullScreen>
              <UserSurveys />
            </FullScreen>
          </RequireAuth>
        }
      />

      <Route
        path="/enrollment/information/*"
        element={
          <RequireAuth>
            <FullScreen>
              <Enrollment />
            </FullScreen>
          </RequireAuth>
        }
      />

      <Route path={ROUTES.PROOF_OF_COMPLETION.INDEX} element={<ProofOfCompletion />} />

      <Route
        path="/partners/:partner/*"
        element={
          <FullScreen>
            <Partners />
          </FullScreen>
        }
      />

      <Route path="/subscriptions/*" element={<Subscriptions />} />

      <Route path="/teams/:teamId/*" element={<Teams />} />

      <Route path="/welcome/*" element={<Welcome />} />
    </Routes>
  );
}

export default Routing;
